import { useEffect, useState } from 'react';
import { TableRow, TableCell, Autocomplete, TextField, Select, MenuItem, IconButton, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { productLabel } from '../../utils/slugify';
import { COMPONENT_STATUSES, COMPONENT_STATUS_LABEL } from '../../utils/constants';

// FEAT-002 ADR-003: 상태 enum 공통 상수 (utils/constants.js) 참조.
// qa / rc 가 신규 추가됨 — 기존 4종 (planned/wip/done/blocked) + qa/rc = 6종.

// release history 캐시 — 같은 SW 반복 선택 시 API 재호출 회피 (component-level 메모리).
const releaseHistoryCache = new Map();

export default function ComponentRow({ component, ci, products, onChange, onRemove }) {
  const product = products.find((p) => p.id === component.product) || null;

  // SW 별 release history (lazy fetch on product 선택)
  const [history, setHistory] = useState(() => releaseHistoryCache.get(component.product) || null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const name = component.product;
    if (!name) {
      setHistory(null);
      return;
    }
    if (releaseHistoryCache.has(name)) {
      setHistory(releaseHistoryCache.get(name));
      return;
    }
    let aborted = false;
    setHistoryLoading(true);
    fetch(`/api/softwares/releases/${encodeURIComponent(name)}?limit=30`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (aborted) return;
        const releases = (data && data.ok && Array.isArray(data.releases)) ? data.releases : [];
        releaseHistoryCache.set(name, releases);
        setHistory(releases);
      })
      .catch(() => {
        if (!aborted) setHistory([]);
      })
      .finally(() => {
        if (!aborted) setHistoryLoading(false);
      });
    return () => { aborted = true; };
  }, [component.product]);

  // 버전 옵션 빌드:
  // 1) latest (📦) + planned (🗓) — softwares.yaml 기준 (기존)
  // 2) 그 외 history (🏷) — GitHub releases 동적 fetch (신규, Bug #2 fix)
  // 3) 사용자 수동 입력값 — 위에 없으면 unshift (수동 표시)
  const versionOptions = [];
  const seen = new Set();

  if (product?.versions?.latest?.tag) {
    const l = product.versions.latest;
    versionOptions.push({
      tag: l.tag,
      label: `📦 ${l.tag} — released${l.published ? ` (${l.published})` : ''}`,
    });
    seen.add(l.tag);
  }
  (product?.versions?.planned || []).forEach((p) => {
    if (seen.has(p.tag)) return;
    const meta = [p.eta && `~ ${p.eta}`, p.note].filter(Boolean).join(', ');
    versionOptions.push({ tag: p.tag, label: `🗓 ${p.tag} — planned${meta ? ` (${meta})` : ''}` });
    seen.add(p.tag);
  });
  (history || []).forEach((r) => {
    if (seen.has(r.tag)) return;
    versionOptions.push({
      tag: r.tag,
      label: `🏷 ${r.tag}${r.published ? ` (${r.published})` : ''}${r.prerelease ? ' · prerelease' : ''}`,
    });
    seen.add(r.tag);
  });
  if (component.version && !seen.has(component.version)) {
    versionOptions.unshift({ tag: component.version, label: `${component.version} (수동)` });
  }

  return (
    <TableRow>
      <TableCell sx={{ width: '35%' }}>
        <Autocomplete
          size="small"
          options={products}
          value={product}
          onChange={(_, newProduct) => onChange(ci, 'product', newProduct?.id || '')}
          getOptionLabel={(p) => productLabel(p)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} placeholder="소프트웨어 검색..." />}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return opts.filter((p) => {
              const l = productLabel(p).toLowerCase();
              return l.includes(q) || p.id.toLowerCase().includes(q);
            });
          }}
        />
      </TableCell>
      <TableCell sx={{ width: '30%' }}>
        <Autocomplete
          size="small"
          freeSolo
          options={versionOptions}
          value={versionOptions.find((o) => o.tag === component.version) || component.version || null}
          onChange={(_, v) => {
            const tag = typeof v === 'string' ? v : v?.tag || '';
            onChange(ci, 'version', tag);
          }}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') onChange(ci, 'version', v);
          }}
          // getOptionLabel: input 에 표시될 텍스트 — `tag` 만 (예: "v0.1.1").
          // 이전엔 `o.label` (예: "v0.1.1 (수동)") 을 반환했으나, freeSolo 모드에서
          // input 표시값이 label 그대로 → 다음 keystroke 가 label 텍스트를 그대로
          // 이어받아 "(수동)" 이 누적 (예: "v (수동)0 (수동)...") 되는 버그 발생.
          getOptionLabel={(o) => typeof o === 'string' ? o : (o.tag || '')}
          // renderOption: 드롭다운 목록 표시 — 풍부한 label (📦 / 🗓 / (수동) 마커 포함)
          renderOption={(props, o) => <li {...props} key={o.tag}>{o.label}</li>}
          isOptionEqualToValue={(a, b) => (a.tag || a) === (b.tag || b)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={historyLoading ? 'release 목록 로드 중...' : '버전 선택 또는 입력'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {historyLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </TableCell>
      <TableCell sx={{ width: '15%' }}>
        <Select
          size="small"
          fullWidth
          value={component.status || 'planned'}
          onChange={(e) => onChange(ci, 'status', e.target.value)}
        >
          {COMPONENT_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {COMPONENT_STATUS_LABEL[s]} ({s})
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={component.notes || ''}
          onChange={(e) => onChange(ci, 'notes', e.target.value)}
          placeholder="메모"
        />
      </TableCell>
      <TableCell sx={{ width: 50 }}>
        <IconButton size="small" color="error" onClick={() => onRemove(ci)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
